import { Controller } from "@hotwired/stimulus"

const LEAFLET_ASSETS = {
  css: [
    "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css",
    "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css",
    "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css"
  ],
  js: [
    "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js",
    "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js"
  ]
}

let leafletLoaded = null
function loadLeaflet() {
  if (leafletLoaded) return leafletLoaded
  leafletLoaded = new Promise((resolve) => {
    LEAFLET_ASSETS.css.forEach(href => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = href
        document.head.appendChild(link)
      }
    })

    const loadScripts = (urls, idx = 0) => {
      if (idx >= urls.length) { resolve(); return }
      const src = urls[idx]
      if (document.querySelector(`script[src="${src}"]`)) { loadScripts(urls, idx + 1); return }
      const script = document.createElement("script")
      script.src = src
      script.onload = () => loadScripts(urls, idx + 1)
      document.head.appendChild(script)
    }
    loadScripts(LEAFLET_ASSETS.js)
  })
  return leafletLoaded
}

export default class extends Controller {
  static targets = ["container", "grid", "toggle", "split", "sidebar"]
  static values = { items: Array, center: Array }

  connect() {
    const params = new URLSearchParams(window.location.search)
    if (params.get("view") === "map") this.show()
  }

  toggle() {
    this.visible ? this.hide() : this.show()
  }

  async show() {
    this.visible = true
    this.gridTarget.classList.add("hidden")
    this.splitTarget.classList.remove("hidden")
    if (!this.map) {
      await loadLeaflet()
      this.initMap()
    }
    setTimeout(() => this.map.invalidateSize(), 100)
    if (this.hasToggleTarget) {
      this.toggleTarget.classList.add("active")
      this.toggleTarget.querySelector("span").textContent = "Grid"
    }

    const url = new URL(window.location)
    url.searchParams.set("view", "map")
    history.replaceState(null, "", url)
  }

  hide() {
    this.visible = false
    this.splitTarget.classList.add("hidden")
    this.gridTarget.classList.remove("hidden")
    if (this.hasToggleTarget) {
      this.toggleTarget.classList.remove("active")
      this.toggleTarget.querySelector("span").textContent = "Map"
    }

    const url = new URL(window.location)
    url.searchParams.delete("view")
    history.replaceState(null, "", url)
  }

  highlightPin(event) {
    const idx = parseInt(event.params.index)
    const marker = this.markers[idx]
    if (!marker) return
    marker.setIcon(this.highlightIcon)
    marker.setZIndexOffset(1000)

    const card = event.currentTarget
    card.classList.add("map-sidebar-card--active")
  }

  unhighlightPin(event) {
    const idx = parseInt(event.params.index)
    const marker = this.markers[idx]
    if (!marker) return
    marker.setIcon(this.defaultIcon)
    marker.setZIndexOffset(0)

    const card = event.currentTarget
    card.classList.remove("map-sidebar-card--active")
  }

  initMap() {
    const L = window.L
    if (!L) return

    const pins = this.itemsValue.filter(i => i.lat && i.lng)
    const center = this.centerValue.length === 2 ? this.centerValue : [34.0, -118.7]

    this.map = L.map(this.containerTarget, { scrollWheelZoom: true }).setView(center, 12)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OSM",
      maxZoom: 18
    }).addTo(this.map)

    this.defaultIcon = L.divIcon({
      className: "map-marker",
      html: '<svg width="32" height="42" viewBox="0 0 32 42"><path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="#0EA5E9" stroke="#0284C7" stroke-width="1"/><circle cx="16" cy="15" r="6" fill="#fff"/></svg>',
      iconSize: [32, 42],
      iconAnchor: [16, 42],
      popupAnchor: [0, -42]
    })

    this.highlightIcon = L.divIcon({
      className: "map-marker map-marker--highlight",
      html: '<svg width="40" height="52" viewBox="0 0 32 42"><path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="#0284C7" stroke="#0369A1" stroke-width="1.5"/><circle cx="16" cy="15" r="6" fill="#fff"/></svg>',
      iconSize: [40, 52],
      iconAnchor: [20, 52],
      popupAnchor: [0, -52]
    })

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 50,
      iconCreateFunction: (c) => {
        const count = c.getChildCount()
        return L.divIcon({
          html: `<div class="map-cluster">${count}</div>`,
          className: "map-cluster-icon",
          iconSize: [40, 40]
        })
      }
    })

    this.markers = []
    const bounds = []
    pins.forEach(item => {
      const marker = L.marker([item.lat, item.lng], { icon: this.defaultIcon })
      const photoHtml = item.photo
        ? `<img src="${item.photo}" class="map-popup-photo" alt="">`
        : ""
      const detailHtml = item.detail
        ? `<div class="map-popup-detail">${item.detail}</div>`
        : ""
      const popup = `<div class="map-popup-card">${photoHtml}<div class="map-popup-body"><strong>${item.title}</strong>${item.subtitle ? `<div class="map-popup-sub">${item.subtitle}</div>` : ""}${detailHtml}<a href="${item.url}" target="_blank">View &rarr;</a></div></div>`
      marker.bindPopup(popup, { minWidth: 200, maxWidth: 240, className: "map-popup-wrap" })

      // When marker is clicked, highlight the sidebar card
      marker.on("click", () => {
        const idx = this.markers.indexOf(marker)
        if (idx >= 0 && this.hasSidebarTarget) {
          const cards = this.sidebarTarget.querySelectorAll(".map-sidebar-card")
          if (cards[idx]) cards[idx].scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
      })

      cluster.addLayer(marker)
      this.markers.push(marker)
      bounds.push([item.lat, item.lng])
    })

    this.map.addLayer(cluster)

    if (bounds.length > 1) {
      this.map.fitBounds(bounds, { padding: [50, 50] })
    } else if (bounds.length === 1) {
      this.map.setView(bounds[0], 14)
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
